import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home, FileText, Image, Phone, MapPin, Save, Plus, Trash2,
  Upload, ChevronLeft, Edit3, X, Info,
  Loader2, Camera, Mountain, Star, Mail, Globe,
  ChevronUp, ChevronDown, Library
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useSiteContent } from '../context/SiteContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizeUrl = (url) =>
  url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

/* ─── Library Picker Modal ─── */
const LibraryModal = ({ onSelect, onClose }) => {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/uploads`)
      .then(r => setUploads(r.data || []))
      .catch(() => toast.error('Failed to load media library'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-void/80 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-void-paper border border-gold-500/20 w-full max-w-3xl max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gold-500/10">
          <h3 className="font-heading text-lg flex items-center gap-2">
            <Library size={18} className="text-gold-500" /> Media Library
          </h3>
          <button onClick={onClose} className="p-1 hover:text-gold-500"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gold-500" size={32} /></div>
          ) : uploads.length === 0 ? (
            <p className="text-center text-foreground/40 py-12">No images uploaded yet. Upload images via the Media tab in Admin.</p>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {uploads.map(img => (
                <button
                  key={img.id}
                  onClick={() => { onSelect(img.url); onClose(); }}
                  className="relative group border border-gold-500/10 hover:border-gold-500/50 transition-all overflow-hidden"
                >
                  <img src={normalizeUrl(img.url)} alt="" className="w-full h-20 object-cover" />
                  <div className="absolute inset-0 bg-gold-500/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-xs font-medium text-white bg-black/60 px-2 py-1">Select</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Image Grid with reorder + library ─── */
const ImageGrid = ({ images = [], onUpdate, uploading, onUpload, tallImages = false }) => {
  const [showLibrary, setShowLibrary] = useState(false);

  const move = (from, to) => {
    const arr = [...images];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    onUpdate(arr);
  };

  const remove = (index) => {
    onUpdate(images.filter((_, i) => i !== index));
  };

  return (
    <>
      {showLibrary && (
        <LibraryModal
          onSelect={url => onUpdate([...images, url])}
          onClose={() => setShowLibrary(false)}
        />
      )}
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mb-3`}>
        {images.map((img, index) => (
          <div key={index} className={`relative group ${tallImages ? 'h-32' : 'h-24'}`}>
            <img src={normalizeUrl(img)} alt="" className="w-full h-full object-cover" />
            {/* Reorder arrows */}
            <div className="absolute left-1 top-1/2 -translate-y-1/2 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              {index > 0 && (
                <button
                  onClick={() => move(index, index - 1)}
                  className="p-0.5 bg-black/60 text-white hover:bg-gold-500/80 transition-colors"
                  title="Move left"
                >
                  <ChevronUp size={12} />
                </button>
              )}
              {index < images.length - 1 && (
                <button
                  onClick={() => move(index, index + 1)}
                  className="p-0.5 bg-black/60 text-white hover:bg-gold-500/80 transition-colors"
                  title="Move right"
                >
                  <ChevronDown size={12} />
                </button>
              )}
            </div>
            {/* Delete */}
            <button
              onClick={() => remove(index)}
              className="absolute top-1 right-1 p-1 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={12} />
            </button>
            <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1">{index + 1}</div>
          </div>
        ))}

        {/* Upload new */}
        <label className={`flex flex-col items-center justify-center ${tallImages ? 'h-32' : 'h-24'} border border-dashed border-gold-500/30 cursor-pointer hover:bg-gold-500/5 transition-all`}>
          {uploading ? <Loader2 size={18} className="animate-spin text-gold-500/50" /> : <Upload size={18} className="text-gold-500/50" />}
          <span className="text-xs text-foreground/50 mt-1">Upload</span>
          <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} />
        </label>
      </div>
      <button
        onClick={() => setShowLibrary(true)}
        className="flex items-center gap-2 text-sm text-gold-500/70 hover:text-gold-500 transition-colors mb-1"
      >
        <Library size={14} /> Pick from media library
      </button>
      <p className="text-xs text-foreground/40">Hover an image to reorder (↑↓) or delete it. First image appears first in the carousel.</p>
    </>
  );
};

/* ─── Main ContentManager ─── */
const ContentManager = () => {
  const { user, isAdmin, authLoading, logout } = useAuth();
  const { refreshContent } = useSiteContent() || {};
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState('homepage');
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/auth');
      } else if (!isAdmin) {
        navigate('/dashboard');
        toast.error('Admin access required');
      } else {
        fetchContent();
      }
    }
  }, [user, authLoading, isAdmin, navigate]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/content`);
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (key) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${key}`, content[key]);
      toast.success(`${key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')} saved!`);
      if (refreshContent) refreshContent();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, callback) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'cms');
    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded!');
      callback(response.data.url);
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const updateContent = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  // field=null means section itself is the array (e.g. testimonials, homepage_faqs)
  const updateNestedContent = (section, field, index, subfield, value) => {
    setContent(prev => {
      const items = field
        ? [...(prev[section]?.[field] || [])]
        : [...(prev[section] || [])];
      items[index] = { ...items[index], [subfield]: value };
      return field
        ? { ...prev, [section]: { ...prev[section], [field]: items } }
        : { ...prev, [section]: items };
    });
  };

  const addArrayItem = (section, field, defaultItem) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: [...(prev[section]?.[field] || []), defaultItem]
      }
    }));
  };

  const removeArrayItem = (section, field, index) => {
    setContent(prev => {
      if (!field) {
        return { ...prev, [section]: (prev[section] || []).filter((_, i) => i !== index) };
      }
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: prev[section]?.[field]?.filter((_, i) => i !== index) || []
        }
      };
    });
  };

  const SaveBtn = ({ sectionKey }) => (
    <button onClick={() => handleSave(sectionKey)} className="btn-primary flex items-center gap-2" disabled={saving}>
      {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
      Save Changes
    </button>
  );

  const sections = [
    { id: 'homepage', label: 'Homepage', icon: Home },
    { id: 'testimonials', label: 'Testimonials', icon: Star },
    { id: 'homepage_faqs', label: 'FAQs', icon: Info },
    { id: 'about', label: 'About', icon: Info },
    { id: 'experiences', label: 'Experiences', icon: Mountain },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'contact', label: 'Contact', icon: Phone },
    { id: 'footer', label: 'Footer', icon: FileText },
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="animate-spin text-gold-500" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-void">
      <div className="max-w-7xl mx-auto container-padding py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium">
              Content <span className="text-gold-500">Manager</span>
            </h1>
            <p className="text-foreground/60 mt-1">Edit website content without touching code</p>
          </div>
          <button onClick={() => navigate('/admin')} className="btn-outline flex items-center gap-2">
            <ChevronLeft size={18} />
            Back to Admin
          </button>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-500/10 border border-blue-500/20 p-4 mb-8 flex items-start gap-3">
          <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-300 font-medium">Safe Content Editing</p>
            <p className="text-foreground/60 text-sm">Changes here update the live website. Bookings, rooms, and settings are managed separately in the Admin panel.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="card-glass p-4 space-y-2 sticky top-24">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${
                    activeSection === section.id
                      ? 'bg-gold-500 text-black font-medium'
                      : 'hover:bg-white/5 text-foreground/70'
                  }`}
                >
                  <section.icon size={18} />
                  {section.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content Editor */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-glass p-6"
            >

              {/* HOMEPAGE SECTION */}
              {activeSection === 'homepage' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-heading flex items-center gap-2">
                      <Home size={20} className="text-gold-500" /> Homepage Content
                    </h2>
                    <SaveBtn sectionKey="homepage" />
                  </div>

                  <div className="grid gap-6">
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Hero Title</label>
                      <input
                        type="text"
                        value={content.homepage?.hero_title || ''}
                        onChange={(e) => updateContent('homepage', 'hero_title', e.target.value)}
                        className="w-full input-default px-4"
                        placeholder="Rudragarh"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Hero Subtitle</label>
                      <input
                        type="text"
                        value={content.homepage?.hero_subtitle || ''}
                        onChange={(e) => updateContent('homepage', 'hero_subtitle', e.target.value)}
                        className="w-full input-default px-4"
                        placeholder="A Legacy of Grace"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Hero Description</label>
                      <textarea
                        value={content.homepage?.hero_description || ''}
                        onChange={(e) => updateContent('homepage', 'hero_description', e.target.value)}
                        className="w-full input-default px-4 py-3"
                        rows={3}
                        placeholder="A Luxury Adventure Retreat..."
                      />
                    </div>

                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Hero Images (carousel)</label>
                      <ImageGrid
                        images={content.homepage?.hero_images || []}
                        onUpdate={(imgs) => updateContent('homepage', 'hero_images', imgs)}
                        uploading={uploading}
                        onUpload={(e) => handleImageUpload(e, (url) => updateContent('homepage', 'hero_images', [...(content.homepage?.hero_images || []), url]))}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Welcome Section Title</label>
                      <input
                        type="text"
                        value={content.homepage?.welcome_title || ''}
                        onChange={(e) => updateContent('homepage', 'welcome_title', e.target.value)}
                        className="w-full input-default px-4"
                        placeholder="Welcome to Rudragarh"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Welcome Text</label>
                      <textarea
                        value={content.homepage?.welcome_text || ''}
                        onChange={(e) => updateContent('homepage', 'welcome_text', e.target.value)}
                        className="w-full input-default px-4 py-3"
                        rows={4}
                        placeholder="Nestled in the golden sands..."
                      />
                    </div>

                    <div className="border-t border-gold-500/10 pt-6">
                      <p className="text-sm font-medium text-gold-500/80 mb-4">"The Experience" Section</p>
                      <div className="grid gap-4">
                        <div>
                          <label className="text-sm text-foreground/60 mb-2 block">Section Headline</label>
                          <input
                            type="text"
                            value={content.homepage?.usp_title || ''}
                            onChange={(e) => updateContent('homepage', 'usp_title', e.target.value)}
                            className="w-full input-default px-4"
                            placeholder="Earthy. Exclusive. Experiential."
                          />
                        </div>
                        <div>
                          <label className="text-sm text-foreground/60 mb-2 block">Section Description</label>
                          <textarea
                            value={content.homepage?.usp_description || ''}
                            onChange={(e) => updateContent('homepage', 'usp_description', e.target.value)}
                            className="w-full input-default px-4 py-3"
                            rows={3}
                            placeholder="Rudragarh offers an unparalleled escape..."
                          />
                        </div>
                        <div>
                          <label className="text-sm text-foreground/60 mb-2 block">Side Image</label>
                          <ImageGrid
                            images={content.homepage?.usp_image ? [content.homepage.usp_image] : []}
                            onUpdate={(imgs) => updateContent('homepage', 'usp_image', imgs[imgs.length - 1] || '')}
                            uploading={uploading}
                            onUpload={(e) => handleImageUpload(e, (url) => updateContent('homepage', 'usp_image', url))}
                            tallImages
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm text-foreground/60 mb-2 block">Image Caption</label>
                            <input
                              type="text"
                              value={content.homepage?.usp_image_caption || ''}
                              onChange={(e) => updateContent('homepage', 'usp_image_caption', e.target.value)}
                              className="w-full input-default px-4"
                              placeholder="The Catacomb Suite"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-foreground/60 mb-2 block">Image Subcaption</label>
                            <input
                              type="text"
                              value={content.homepage?.usp_image_subcaption || ''}
                              onChange={(e) => updateContent('homepage', 'usp_image_subcaption', e.target.value)}
                              className="w-full input-default px-4"
                              placeholder="One-of-a-kind experience"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TESTIMONIALS SECTION */}
              {activeSection === 'testimonials' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-heading flex items-center gap-2">
                      <Star size={20} className="text-gold-500" /> Guest Testimonials
                    </h2>
                    <SaveBtn sectionKey="testimonials" />
                  </div>
                  <p className="text-foreground/50 text-sm">These appear in the "What Our Guests Say" section on the homepage.</p>

                  <div className="space-y-4">
                    {(content.testimonials || []).map((t, index) => (
                      <div key={index} className="p-4 bg-void-subtle border border-gold-500/10">
                        <div className="flex justify-between items-start mb-3">
                          <span className="text-gold-500 text-sm font-medium">Review #{index + 1}</span>
                          <button
                            onClick={() => removeArrayItem('testimonials', null, index)}
                            className="p-1 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="grid gap-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-foreground/50 mb-1 block">Guest Name</label>
                              <input
                                type="text"
                                value={t.name || ''}
                                onChange={(e) => updateNestedContent('testimonials', null, index, 'name', e.target.value)}
                                className="w-full input-default px-3 py-2 text-sm"
                                placeholder="Name"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-foreground/50 mb-1 block">Location</label>
                              <input
                                type="text"
                                value={t.location || ''}
                                onChange={(e) => updateNestedContent('testimonials', null, index, 'location', e.target.value)}
                                className="w-full input-default px-3 py-2 text-sm"
                                placeholder="City"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-foreground/50 mb-1 block">Occasion</label>
                              <input
                                type="text"
                                value={t.occasion || ''}
                                onChange={(e) => updateNestedContent('testimonials', null, index, 'occasion', e.target.value)}
                                className="w-full input-default px-3 py-2 text-sm"
                                placeholder="e.g. Honeymoon"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-foreground/50 mb-1 block">Rating (1–5)</label>
                              <select
                                value={t.rating || 5}
                                onChange={(e) => updateNestedContent('testimonials', null, index, 'rating', parseInt(e.target.value))}
                                className="w-full input-default px-3 py-2 text-sm"
                              >
                                {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{r} Stars</option>)}
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-foreground/50 mb-1 block">Review Text</label>
                            <textarea
                              value={t.text || ''}
                              onChange={(e) => updateNestedContent('testimonials', null, index, 'text', e.target.value)}
                              className="w-full input-default px-3 py-2 text-sm"
                              rows={3}
                              placeholder="What the guest said..."
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const cur = content.testimonials || [];
                        setContent(prev => ({ ...prev, testimonials: [...cur, { name: '', location: '', text: '', rating: 5, occasion: '' }] }));
                      }}
                      className="w-full p-3 border border-dashed border-gold-500/30 text-gold-500 hover:bg-gold-500/5 flex items-center justify-center gap-2"
                    >
                      <Plus size={18} /> Add Testimonial
                    </button>
                  </div>
                </div>
              )}

              {/* HOMEPAGE FAQs SECTION */}
              {activeSection === 'homepage_faqs' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-heading flex items-center gap-2">
                      <Info size={20} className="text-gold-500" /> Homepage FAQs
                    </h2>
                    <SaveBtn sectionKey="homepage_faqs" />
                  </div>
                  <p className="text-foreground/50 text-sm">These appear in the "Frequently Asked" section at the bottom of the homepage.</p>

                  <div className="space-y-4">
                    {(content.homepage_faqs || []).map((faq, index) => (
                      <div key={index} className="p-4 bg-void-subtle border border-gold-500/10">
                        <div className="flex justify-between items-start mb-3">
                          <span className="text-gold-500 text-sm font-medium">FAQ #{index + 1}</span>
                          <button
                            onClick={() => {
                              const arr = [...(content.homepage_faqs || [])];
                              arr.splice(index, 1);
                              setContent(prev => ({ ...prev, homepage_faqs: arr }));
                            }}
                            className="p-1 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="grid gap-3">
                          <div>
                            <label className="text-xs text-foreground/50 mb-1 block">Question</label>
                            <input
                              type="text"
                              value={faq.q || ''}
                              onChange={(e) => {
                                const arr = [...(content.homepage_faqs || [])];
                                arr[index] = { ...arr[index], q: e.target.value };
                                setContent(prev => ({ ...prev, homepage_faqs: arr }));
                              }}
                              className="w-full input-default px-3 py-2 text-sm"
                              placeholder="Question"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-foreground/50 mb-1 block">Answer</label>
                            <textarea
                              value={faq.a || ''}
                              onChange={(e) => {
                                const arr = [...(content.homepage_faqs || [])];
                                arr[index] = { ...arr[index], a: e.target.value };
                                setContent(prev => ({ ...prev, homepage_faqs: arr }));
                              }}
                              className="w-full input-default px-3 py-2 text-sm"
                              rows={3}
                              placeholder="Answer"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => setContent(prev => ({ ...prev, homepage_faqs: [...(prev.homepage_faqs || []), { q: '', a: '' }] }))}
                      className="w-full p-3 border border-dashed border-gold-500/30 text-gold-500 hover:bg-gold-500/5 flex items-center justify-center gap-2"
                    >
                      <Plus size={18} /> Add FAQ
                    </button>
                  </div>
                </div>
              )}

              {/* ABOUT SECTION */}
              {activeSection === 'about' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-heading flex items-center gap-2">
                      <Info size={20} className="text-gold-500" /> About Page Content
                    </h2>
                    <SaveBtn sectionKey="about" />
                  </div>

                  <div className="grid gap-6">
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Page Title</label>
                      <input type="text" value={content.about?.title || ''} onChange={(e) => updateContent('about', 'title', e.target.value)} className="w-full input-default px-4" placeholder="Our Story" />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Subtitle</label>
                      <input type="text" value={content.about?.subtitle || ''} onChange={(e) => updateContent('about', 'subtitle', e.target.value)} className="w-full input-default px-4" placeholder="A Legacy of Hospitality" />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Description</label>
                      <textarea value={content.about?.description || ''} onChange={(e) => updateContent('about', 'description', e.target.value)} className="w-full input-default px-4 py-3" rows={5} placeholder="Tell your story..." />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Mission Statement</label>
                      <textarea value={content.about?.mission || ''} onChange={(e) => updateContent('about', 'mission', e.target.value)} className="w-full input-default px-4 py-3" rows={3} placeholder="Our mission..." />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Highlights</label>
                      {content.about?.highlights?.map((highlight, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={highlight}
                            onChange={(e) => {
                              const newHighlights = [...content.about.highlights];
                              newHighlights[index] = e.target.value;
                              updateContent('about', 'highlights', newHighlights);
                            }}
                            className="flex-1 input-default px-4"
                          />
                          <button onClick={() => removeArrayItem('about', 'highlights', index)} className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                      <button onClick={() => addArrayItem('about', 'highlights', '')} className="flex items-center gap-2 text-gold-500 hover:text-gold-400 text-sm">
                        <Plus size={16} /> Add Highlight
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* EXPERIENCES SECTION */}
              {activeSection === 'experiences' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-heading flex items-center gap-2">
                      <Mountain size={20} className="text-gold-500" /> Experiences Content
                    </h2>
                    <SaveBtn sectionKey="experiences" />
                  </div>

                  <div className="grid gap-6">
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Page Title</label>
                      <input type="text" value={content.experiences?.title || ''} onChange={(e) => updateContent('experiences', 'title', e.target.value)} className="w-full input-default px-4" placeholder="Desert Experiences" />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Subtitle</label>
                      <input type="text" value={content.experiences?.subtitle || ''} onChange={(e) => updateContent('experiences', 'subtitle', e.target.value)} className="w-full input-default px-4" placeholder="Adventures Await" />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Description</label>
                      <textarea value={content.experiences?.description || ''} onChange={(e) => updateContent('experiences', 'description', e.target.value)} className="w-full input-default px-4 py-3" rows={3} />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-3 block">Experience Items</label>
                      <div className="space-y-4">
                        {content.experiences?.items?.map((item, index) => (
                          <div key={index} className="p-4 bg-void-subtle border border-gold-500/10">
                            <div className="flex justify-between items-start mb-3">
                              <span className="text-gold-500 text-sm">Experience #{index + 1}</span>
                              <button onClick={() => removeArrayItem('experiences', 'items', index)} className="p-1 text-red-400 hover:bg-red-500/20"><Trash2 size={14} /></button>
                            </div>
                            <div className="grid gap-3">
                              <input type="text" value={item.title || ''} onChange={(e) => updateNestedContent('experiences', 'items', index, 'title', e.target.value)} className="w-full input-default px-4" placeholder="Experience Title" />
                              <textarea value={item.description || ''} onChange={(e) => updateNestedContent('experiences', 'items', index, 'description', e.target.value)} className="w-full input-default px-4 py-2" rows={2} placeholder="Description" />
                              <div className="flex items-center gap-2">
                                {item.image && <img src={item.image} alt="" className="w-16 h-16 object-cover" />}
                                <label className="flex-1 flex items-center justify-center gap-2 p-2 border border-dashed border-gold-500/30 cursor-pointer hover:bg-gold-500/5">
                                  <Camera size={16} className="text-gold-500/50" />
                                  <span className="text-sm text-foreground/50">{item.image ? 'Change' : 'Add'} Image</span>
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => updateNestedContent('experiences', 'items', index, 'image', url))} />
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}
                        <button onClick={() => addArrayItem('experiences', 'items', { title: '', description: '', image: '' })} className="w-full p-3 border border-dashed border-gold-500/30 text-gold-500 hover:bg-gold-500/5 flex items-center justify-center gap-2">
                          <Plus size={18} /> Add Experience
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* GALLERY SECTION */}
              {activeSection === 'gallery' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-heading flex items-center gap-2">
                      <Image size={20} className="text-gold-500" /> Gallery Content
                    </h2>
                    <SaveBtn sectionKey="gallery" />
                  </div>

                  <div className="grid gap-6">
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Page Title</label>
                      <input type="text" value={content.gallery?.title || ''} onChange={(e) => updateContent('gallery', 'title', e.target.value)} className="w-full input-default px-4" placeholder="Gallery" />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Subtitle</label>
                      <input type="text" value={content.gallery?.subtitle || ''} onChange={(e) => updateContent('gallery', 'subtitle', e.target.value)} className="w-full input-default px-4" placeholder="Moments at Rudragarh" />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-3 block">Gallery Images</label>
                      <ImageGrid
                        images={content.gallery?.images || []}
                        onUpdate={(imgs) => updateContent('gallery', 'images', imgs)}
                        uploading={uploading}
                        onUpload={(e) => handleImageUpload(e, (url) => updateContent('gallery', 'images', [...(content.gallery?.images || []), url]))}
                        tallImages
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* CONTACT SECTION */}
              {activeSection === 'contact' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-heading flex items-center gap-2">
                      <Phone size={20} className="text-gold-500" /> Contact Information
                    </h2>
                    <SaveBtn sectionKey="contact" />
                  </div>
                  <p className="text-foreground/50 text-sm">Changes here update the phone number, address, and email shown in the Footer, Contact page, and floating buttons across the entire site.</p>

                  <div className="grid gap-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-foreground/60 mb-2 block">Phone Number</label>
                        <input type="tel" value={content.contact?.phone || ''} onChange={(e) => updateContent('contact', 'phone', e.target.value)} className="w-full input-default px-4" placeholder="+91 96496 11111" />
                        <p className="text-xs text-foreground/40 mt-1">Include country code, e.g. +91 96496 11111</p>
                      </div>
                      <div>
                        <label className="text-sm text-foreground/60 mb-2 block">WhatsApp Number</label>
                        <input type="tel" value={content.contact?.whatsapp || ''} onChange={(e) => updateContent('contact', 'whatsapp', e.target.value)} className="w-full input-default px-4" placeholder="+91 96496 11111" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Email Address</label>
                      <input type="email" value={content.contact?.email || ''} onChange={(e) => updateContent('contact', 'email', e.target.value)} className="w-full input-default px-4" placeholder="info@rudragarh.com" />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Full Address</label>
                      <textarea value={content.contact?.address || ''} onChange={(e) => updateContent('contact', 'address', e.target.value)} className="w-full input-default px-4 py-3" rows={2} placeholder="Rudragarh Resort, Near Sam Sand Dunes..." />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">UPI ID (for payments)</label>
                      <input type="text" value={content.contact?.upi_id || ''} onChange={(e) => updateContent('contact', 'upi_id', e.target.value)} className="w-full input-default px-4" placeholder="rudragarh@upi" />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Google Maps Embed URL</label>
                      <input type="text" value={content.contact?.map_embed || ''} onChange={(e) => updateContent('contact', 'map_embed', e.target.value)} className="w-full input-default px-4" placeholder="https://www.google.com/maps/embed?..." />
                      <p className="text-xs text-foreground/40 mt-1">Paste the iframe src URL from Google Maps embed code</p>
                    </div>
                  </div>
                </div>
              )}

              {/* FOOTER SECTION */}
              {activeSection === 'footer' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-heading flex items-center gap-2">
                      <FileText size={20} className="text-gold-500" /> Footer Content
                    </h2>
                    <SaveBtn sectionKey="footer" />
                  </div>

                  <div className="grid gap-6">
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Footer Tagline</label>
                      <input type="text" value={content.footer?.tagline || ''} onChange={(e) => updateContent('footer', 'tagline', e.target.value)} className="w-full input-default px-4" placeholder="A Legacy of Grace" />
                    </div>
                    <div>
                      <label className="text-sm text-foreground/60 mb-2 block">Copyright Text</label>
                      <input type="text" value={content.footer?.copyright || ''} onChange={(e) => updateContent('footer', 'copyright', e.target.value)} className="w-full input-default px-4" placeholder="© 2024 Rudragarh Resort. All rights reserved." />
                    </div>
                  </div>
                </div>
              )}

            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentManager;
